import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '@web/components';
import { Radio, MultiSelect, ButtonLink, Dropdown, Input } from '@web/atoms';
import { Footer } from '../Footer';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { Question } from '../Question';
import { onboardingQuestionSubmitted, onboardingCTAClicked } from '../events';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useGetAllergies } from '../../allergies';
import {
  StyledHeader,
  CtaText,
  FormSubheading,
  ModalContainer,
  ModalHeading,
  ModalContent,
  ModalContentTitle,
  ModalContentBody,
  DropdownWrapper,
  StyledForm,
} from './styles';
import { schema } from './schema';

export { schema } from './schema';

export const NutritionForm = ({
  activityQuestionPrompt,
  allergiesQuestionPrompt,
  allergySearchLabel,
  poopQuestionPrompt,
  poopSearchLabel,
  poopSubheadline,
  goBack,
  onSubmit,
  levels,
  stoolTypes,
  defaultValues,
  modal,
  ctaText,
  userInfoSubheadline,
  emailLabel,
  nameLabel,
  userInfoQuestionPrompt,
  index,
}) => {
  const [shown, setShown] = useState(false);
  const getAllergies = useGetAllergies();
  const reporter = useReporter();
  const {
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    clearErrors,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const questionRefs = useRef([]);
  questionRefs.current = new Array(4)
    .fill(0)
    .map((_, i) => questionRefs.current[i] || React.createRef());

  const handleBlurOrChange = questionIndex => {
    if (questionIndex < questionRefs.current.length - 1) {
      // Scroll to the next question
      questionRefs.current[questionIndex + 1].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    const position = questionIndex + 5;
    reporter.tag(onboardingQuestionSubmitted(position));
  };
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const element = document.querySelectorAll('[role="alert"]');
      //scroll to error
      element[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors]);

  const submitForm = async data => {
    reporter.tag(onboardingCTAClicked(2));
    const result = await onSubmit(data);

    ApiResult.match(result, {
      error: {
        invalidEmail: () =>
          setError(
            'email',
            {
              type: 'server',
              message: 'Email is invalid',
            },
            { shouldFocus: true },
          ),
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <StyledHeader>Your pup’s nutrition</StyledHeader>
      <FormFieldsContainer>
        {/* Activity Question */}
        <Question
          prompt={activityQuestionPrompt}
          bgColor={'background.lightest'}
          ref={questionRefs.current[0]}
        >
          <Controller
            control={control}
            name="activity"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <>
                <Radio
                  {...field}
                  onChange={val => {
                    field.onChange(val);
                    handleBlurOrChange(0);
                  }}
                  choices={levels}
                  direction="column"
                  name="activity"
                  legendText="activity"
                  error={error?.message || ''}
                  status={error?.message ? 'error' : null}
                />
              </>
            )}
          />
        </Question>
        {/* Allergies Question */}
        <Question
          prompt={allergiesQuestionPrompt}
          bgColor={'background.lightest'}
          ref={questionRefs.current[1]}
        >
          <Controller
            control={control}
            name="allergies"
            render={({ field: { ref, ...field } }) => (
              <MultiSelect
                {...field}
                searchLabel={allergySearchLabel}
                values={getAllergies}
                selectedValues={field.value}
                onChange={val => {
                  field.onChange(val);
                  handleBlurOrChange(1);
                }}
              />
            )}
          />
        </Question>
        {/* Poop Question */}
        <Question
          prompt={poopQuestionPrompt}
          bgColor={'background.lightest'}
          ref={questionRefs.current[2]}
        >
          <FormSubheading>{poopSubheadline}</FormSubheading>
          <Controller
            control={control}
            name="stoolType"
            render={({ field: { ref, ...field } }) => (
              <DropdownWrapper>
                <Dropdown
                  {...field}
                  noSelectionLabel={poopSearchLabel}
                  options={stoolTypes}
                  selected={field.value}
                  onChange={val => {
                    field.onChange(val);
                    handleBlurOrChange(2);
                  }}
                />
              </DropdownWrapper>
            )}
          />
          <ButtonLink onClick={() => setShown(true)}>
            <CtaText>{ctaText}</CtaText>
          </ButtonLink>
        </Question>
        {/*User Info Question*/}
        <Question
          prompt={userInfoQuestionPrompt}
          bgColor={'background.lightest'}
          ref={questionRefs.current[3]}
        >
          <FormSubheading>{userInfoSubheadline}</FormSubheading>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                error={error?.message}
                labelText={nameLabel}
                status={error?.message ? 'error' : null}
                maxLength={100}
                visuallyHideLabel
                centeredAlign
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                onChange={value => {
                  if (errors.email?.type === 'server') {
                    clearErrors('email');
                  }
                  field.onChange(value.trim());
                }}
                labelText={emailLabel}
                error={error?.message}
                status={error?.message ? 'error' : null}
                maxLength={100}
                visuallyHideLabel
                centeredAlign
                onBlur={val => {
                  field.onChange(val);
                  handleBlurOrChange(3);
                }}
              />
            )}
          />
        </Question>
      </FormFieldsContainer>
      <Footer
        onBackButtonClick={goBack}
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
      />
      <Modal
        ariaLabel="About Stool Modal"
        shown={shown}
        onCloseModal={() => setShown(false)}
      >
        <ModalContainer>
          <ModalHeading>{modal.heading}</ModalHeading>
          {modal.options.map((option, i) => (
            <ModalContent key={i}>
              <ModalContentTitle>
                {option.title.toUpperCase()}
              </ModalContentTitle>
              <ModalContentBody>{option.text}</ModalContentBody>
            </ModalContent>
          ))}
        </ModalContainer>
      </Modal>
    </StyledForm>
  );
};

NutritionForm.displayName = 'NutritionForm';

NutritionForm.propTypes = {
  activityQuestionPrompt: PropTypes.string.isRequired,
  allergiesQuestionPrompt: PropTypes.string.isRequired,
  allergySearchLabel: PropTypes.string.isRequired,
  poopQuestionPrompt: PropTypes.string.isRequired,
  poopSubheadline: PropTypes.string,
  poopSearchLabel: PropTypes.string.isRequired,
  userInfoQuestionPrompt: PropTypes.string.isRequired,
  userInfoSubheadline: PropTypes.string,
  emailLabel: PropTypes.string.isRequired,
  nameLabel: PropTypes.string.isRequired,
  defaultValues: PropTypes.shape({
    activity: PropTypes.string,
    allergies: PropTypes.array,
    stoolType: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  modal: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  stoolTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  onSubmit: PropTypes.func.isRequired,
};
